import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { initializeGA, trackPageView, trackEvent, removeGA } from '../services/analytics';

/**
 * Custom hook for managing Google Analytics
 * Handles initialization, page tracking, and consent management
 * @returns {Object} Analytics state and functions
 */
const useAnalytics = () => {
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize GA based on stored consent
  useEffect(() => {
    const consentStatus = localStorage.getItem('cookieConsent');
    
    if (consentStatus === 'accepted' && !isInitialized) {
      const initialized = initializeGA();
      setIsInitialized(initialized);
    }
  }, [isInitialized]);
  
  // Track page views when location changes
  useEffect(() => {
    if (isInitialized) {
      // Get page title from document or use pathname as fallback
      const pageTitle = document.title || location.pathname;
      trackPageView(location.pathname, pageTitle);
    }
  }, [location, isInitialized]);
  
  // Handle consent changes
  const handleAcceptCookies = () => {
    if (!isInitialized) {
      const initialized = initializeGA();
      setIsInitialized(initialized);
      
      if (initialized) {
        // Track initial page view
        const pageTitle = document.title || location.pathname;
        trackPageView(location.pathname, pageTitle);
      }
    }
  };
  
  const handleDeclineCookies = () => {
    if (isInitialized) {
      removeGA();
      setIsInitialized(false);
    }
  };
  
  // Function to track custom events
  const trackAnalyticsEvent = (action, params = {}) => {
    if (isInitialized) {
      trackEvent(action, params);
    }
  };
  
  return {
    isInitialized,
    handleAcceptCookies,
    handleDeclineCookies,
    trackAnalyticsEvent
  };
};

export default useAnalytics;
